import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '@/config/firebase'

export interface UploadedFile {
  url: string
  name: string
  type: string
  path: string
}

export async function uploadFile(path: string, file: File): Promise<UploadedFile> {
  const storageRef = ref(storage, path)
  const snapshot = await uploadBytes(storageRef, file)
  const url = await getDownloadURL(snapshot.ref)
  return { url, name: file.name, type: file.type, path }
}

export async function uploadCompanyLogo(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png'
  const path = `company/logo.${ext}`
  const result = await uploadFile(path, file)
  return result.url
}

export async function uploadCompanyPdfLogo(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png'
  const path = `company/pdf-logo.${ext}`
  const result = await uploadFile(path, file)
  return result.url
}

export async function uploadCompanyWatermark(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'svg'
  const path = `company/watermark.${ext}`
  const result = await uploadFile(path, file)
  return result.url
}

export async function uploadCompanyLoginLogo(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png'
  const path = `company/login-logo.${ext}`
  const result = await uploadFile(path, file)
  return result.url
}

export async function uploadCompanyFavicon(file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png'
  const path = `company/favicon.${ext}`
  const result = await uploadFile(path, file)
  return result.url
}

export async function uploadExpenseReceipt(expenseId: string, file: File, index: number): Promise<UploadedFile> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const path = `expenses/${expenseId}/receipt_${index}_${safeName}`
  return uploadFile(path, file)
}

/**
 * Sube un soporte para un movimiento de finanzas (ingreso o egreso).
 * `kind` define la carpeta ('incomes' | 'expenses'), `docId` el registro.
 */
export async function uploadFinanceReceipt(
  kind: 'income' | 'expense',
  docId: string,
  file: File,
  index: number
): Promise<UploadedFile> {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
  const folder = kind === 'income' ? 'incomes' : 'expenses'
  const path = `${folder}/${docId}/receipt_${index}_${safeName}`
  return uploadFile(path, file)
}

/**
 * Reads an image File, scales it down so its largest side is <= maxDim, and
 * returns a base64 PNG data URL. Used to embed logos directly in Firestore so
 * PDFs can render them without cross-origin (CORS) fetches to Storage.
 */
export async function fileToScaledPngDataUrl(file: File, maxDim = 512): Promise<string> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('No se pudo decodificar la imagen'))
    image.src = dataUrl
  })

  const w = img.naturalWidth || 1
  const h = img.naturalHeight || 1
  const scale = Math.min(1, maxDim / Math.max(w, h))
  const outW = Math.max(1, Math.round(w * scale))
  const outH = Math.max(1, Math.round(h * scale))

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')
  if (!ctx) return dataUrl
  ctx.drawImage(img, 0, 0, outW, outH)
  return canvas.toDataURL('image/png')
}

export async function deleteStorageFile(path: string): Promise<void> {
  try {
    await deleteObject(ref(storage, path))
  } catch {
    // File may not exist, ignore
  }
}
